// src/oura/components/visibility/sourceKinds.ts
//
// How a quoted site is drawn: its word and its tinted icon square, one colour
// per kind of site so a list sorts by eye before it is read.

import {
    BookOpenIcon,
    GlobeIcon,
    LinkIcon,
    MapPinIcon,
    MessageSquareIcon,
    NewspaperIcon,
    StarIcon,
    StoreIcon,
    type LucideIcon,
} from "lucide-react";
import type { AeoSourceKind } from "@service/aeo";
import type { Tone } from "@oura/ui";
import type { VisSourceRow } from "../../visibility/model";

export const SOURCE_KIND: Record<AeoSourceKind, { label: string; tone: Tone; icon: LucideIcon }> = {
    map: { label: "Map", tone: "blue", icon: MapPinIcon },
    directory: { label: "Directory", tone: "orange", icon: BookOpenIcon },
    review: { label: "Reviews", tone: "amber", icon: StarIcon },
    media: { label: "Media", tone: "purple", icon: NewspaperIcon },
    social: { label: "Social", tone: "fuchsia", icon: MessageSquareIcon },
    own: { label: "Own site", tone: "teal", icon: GlobeIcon },
    rival: { label: "Competitor", tone: "rose", icon: StoreIcon },
    other: { label: "Other", tone: "gray", icon: LinkIcon },
};

/**
 * The client's words for each kind, for the labelled list (redesign): a
 * plural for the filter, and one line under the domain saying what the site
 * is. The kind is said in words because colour alone said nothing to a
 * reader who had not been told the key.
 */
export const SOURCE_KIND_WORDS: Record<AeoSourceKind, { plural: string; note: string }> = {
    map: { plural: "Maps", note: "A maps listing" },
    directory: { plural: "Directories", note: "Lists and compares businesses" },
    review: { plural: "Reviews", note: "A reviews site" },
    media: { plural: "Media", note: "News or a magazine" },
    social: { plural: "Social", note: "Social media" },
    own: { plural: "Your site", note: "Your website" },
    rival: { plural: "Competitors", note: "Another business like yours" },
    other: { plural: "Other", note: "Not sorted yet" },
};

/** The order the filter lists kinds in: the ones a client can act on first, Other last. */
export const SOURCE_KIND_ORDER: AeoSourceKind[] = ["directory", "review", "map", "social", "media", "rival", "other"];

export const UNKNOWN_SOURCE = { tone: "gray" as Tone, icon: LinkIcon };

/** The row's kind; the own row is the site whether or not the payload classified it. */
export function sourceKind(row: VisSourceRow): AeoSourceKind | null {
    return row.own ? "own" : row.kind;
}
